import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import { OPTION_CATEGORY_INVESTOR } from 'constants/general';
import getValueOption from 'utils/getOptionValue';

import type ISelectSupplierTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onEditRecord,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  onDeleteItem,
  isViewOnly = false,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const filteredHeadCells = headCells.filter(({ id }) => {
    if (id === 'actions') {
      return !isViewOnly;
    }
    return true;
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Supplier table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {filteredHeadCells.map(({
              id, label,
            }, index) => {
              const isLastVisible = isViewOnly && index === filteredHeadCells.length - 1;
              return (
                <TableCell
                  head
                  className={clsx(styles.tableCell, {
                    [styles.lastVisibleColumn]: isLastVisible,
                  })}
                  key={id}
                >
                  <div>
                    <span>{t(label)}</span>
                  </div>
                </TableCell>
              );
            })}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <span>{item.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {item.contactName || hyphen}
                </TableCell>
                <TableCell>
                  {item.contactEmail || hyphen}
                </TableCell>
                <TableCell>
                  {item.equityPercentage || hyphen}
                </TableCell>
                <TableCell>
                  {getValueOption(OPTION_CATEGORY_INVESTOR, item.shareCategory)?.label || hyphen}
                </TableCell>
                <TableCell
                  className={clsx({
                    [styles.lastVisibleColumn]: isViewOnly,
                  })}
                >
                  {item.investmentDuration || hyphen}
                </TableCell>
                {!isViewOnly && (
                  <TableCell>
                    <IconButton
                      icon={<PenIcon />}
                      onClick={() => onEditRecord(item.id)}
                    />
                    <IconButton
                      icon={<TrashIcon />}
                      onClick={() => onDeleteItem?.(item.id)}
                    />
                  </TableCell>
                )}
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTable;
